/**
 * API calls for the Reconciliation Admin module, over the shared `request()`
 * in `src/infrastructure/lib/api.ts`.
 */

import { API_BASE, request } from '@/src/infrastructure/lib/api';
import type {
  DeadLetterOutboxEvent,
  ReconciliationAlertSummary,
  ReconciliationFindingRow,
  ReplayWebhookResponse,
  RequeueOutboxResponse,
  ResolveFindingPayload,
} from '../types';

// Auth, dev bypass, envelope unwrap and ApiError all come from the shared client — one copy to fix.
export { ApiError, errorCode, request } from '@/src/infrastructure/lib/api';

export const reconciliationApi = {
  getAlerts: (): Promise<ReconciliationAlertSummary> => {
    return request<ReconciliationAlertSummary>(`${API_BASE}/admin/reconciliation/alerts`);
  },

  listFindings: (params: { kind?: string; limit?: number } = {}): Promise<{ items: ReconciliationFindingRow[] }> => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.limit) qs.set('limit', String(params.limit));
    const queryString = qs.toString() ? `?${qs.toString()}` : '';
    return request<{ items: ReconciliationFindingRow[] }>(
      `${API_BASE}/admin/reconciliation/findings${queryString}`,
    );
  },

  resolveFinding: (
    findingId: string,
    payload: ResolveFindingPayload,
  ): Promise<ReconciliationFindingRow> => {
    return request<ReconciliationFindingRow>(
      `${API_BASE}/admin/reconciliation/findings/${findingId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
    );
  },

  replayWebhook: (webhookEventId: string): Promise<ReplayWebhookResponse> => {
    return request<ReplayWebhookResponse>(
      `${API_BASE}/admin/webhooks/${webhookEventId}/replay`,
      {
        method: 'POST',
      },
    );
  },

  listDeadLetterOutbox: (limit = 50): Promise<DeadLetterOutboxEvent[]> => {
    return request<DeadLetterOutboxEvent[]>(
      `${API_BASE}/admin/outbox/dead-letter?limit=${limit}`,
    );
  },

  requeueOutbox: (id: string): Promise<RequeueOutboxResponse> => {
    return request<RequeueOutboxResponse>(
      `${API_BASE}/admin/outbox/${id}/requeue`,
      {
        method: 'POST',
      },
    );
  },
};
